import { DEBUG } from '../../config/debug';

/** CRA on :3000 → Vercel API on :3001 (same pattern as notificationService). */
function powerCacheApiBase(): string {
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  return isLocalhost ? 'http://localhost:3001' : '';
}

function powerCacheUrl(sessionId: string): string {
  return `${powerCacheApiBase()}/api/power-cache?sessionId=${encodeURIComponent(sessionId)}`;
}

// Generic cache helper - handles all data types via smart API delegation
export class PowerCacheHelper {
  private static async authHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const { supabase } = await import('../../config/supabase');
      const { data } = await supabase.auth.getSession();
      if (data.session?.access_token) {
        headers.Authorization = `Bearer ${data.session.access_token}`;
      }
    } catch {
      /* non-fatal — anonymous chart cache keys still work */
    }
    return headers;
  }

  private static async callGenericCacheAPI(method: 'GET' | 'POST', sessionId: string, data?: any) {
    try {
      const url = powerCacheUrl(sessionId);
      const options: RequestInit = {
        method,
        headers: await this.authHeaders(),
      };

      if (method === 'POST' && data) {
        options.body = JSON.stringify({ data });
      }

      const response = await fetch(url, options);

      if (response.status === 404 && method === 'GET') {
        // Track cache miss for monitoring (browser only)
        if (typeof window !== 'undefined') {
          try {
            const Sentry = require('@sentry/react');
            Sentry.metrics.increment('cache.miss', 1, {
              tags: {
                endpoint: 'power-cache',
                sessionType: this.getSessionType(sessionId),
                userType: 'beta',
              },
            });
          } catch (sentryError) {
            // Ignore Sentry errors to prevent breaking cache functionality
          }
        }

        // Cache miss is expected, not an error
        return { data: null, fromCache: false };
      }

      if (!response.ok) {
        throw new Error(`Cache API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // Track cache hit for monitoring (browser only)
      if (result.fromCache && typeof window !== 'undefined') {
        try {
          const Sentry = require('@sentry/react');
          Sentry.metrics.increment('cache.hit', 1, {
            tags: {
              endpoint: 'power-cache',
              sessionType: this.getSessionType(sessionId),
              userType: 'beta',
            },
          });
        } catch (sentryError) {
          // Ignore Sentry errors to prevent breaking cache functionality
        }
      }

      return result;
    } catch (error) {
      console.error('[GenericCacheHelper] API call failed:', error);
      // Graceful degradation - return null so app continues working
      return { data: null, fromCache: false, error: error.message };
    }
  }

  private static getSessionType(sessionId: string): string {
    if (sessionId === 'sessions-list' || sessionId.startsWith('sessions-list-'))
      return 'sessions-list';
    if (sessionId.startsWith('power-output-')) return 'power-output';
    if (sessionId.startsWith('swivel-power-time-series-')) return 'swivel-power-time-series';
    if (sessionId.startsWith('comprehensive-data-')) return 'comprehensive-data';
    if (sessionId.startsWith('stroke-rate-')) return 'stroke-rate';
    return 'other';
  }

  static async get(sessionId: string) {
    const result = await this.callGenericCacheAPI('GET', sessionId);
    return result.data;
  }

  static async set(sessionId: string, data: any): Promise<boolean> {
    const result = await this.callGenericCacheAPI('POST', sessionId, data);
    return result.success || false;
  }

  // Add cache invalidation (delete) method
  static async delete(sessionId: string): Promise<boolean> {
    try {
      const url = powerCacheUrl(sessionId);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: await this.authHeaders(),
      });
      // Idempotent purge — missing keys are fine
      if (response.status === 404) {
        return true;
      }
      if (!response.ok) {
        throw new Error(`Cache API error: ${response.status} ${response.statusText}`);
      }
      return true;
    } catch (error) {
      console.error('[GenericCacheHelper] Failed to delete cache:', error);
      return false;
    }
  }

  // Wrapper that mimics the existing withCache pattern
  static async withCache<T>(sessionId: string, fetcher: () => Promise<T>): Promise<T> {
    // Disable cache in development mode to avoid API endpoint issues
    const isDevelopment = process.env.NODE_ENV === 'development';

    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(`[GenericCache] Development mode - bypassing cache for session: ${sessionId}`);
      return fetcher();
    }

    try {
      // Try cache first
      DEBUG.log('POWER', `[GenericCache] Checking cache for session: ${sessionId}`);
      const cached = await this.get(sessionId);

      if (cached) {
        DEBUG.log('POWER', `[GenericCache] Using cached data for session: ${sessionId}`);
        return cached as T;
      }

      // Fetch fresh data - track performance when cache misses
      DEBUG.log('POWER', `[GenericCache] Fetching fresh data for session: ${sessionId}`);
      const fetchStartTime = performance.now();

      const data = await fetcher();

      // Track cache miss fetch time (browser only)
      if (typeof window !== 'undefined') {
        try {
          const fetchDuration = performance.now() - fetchStartTime;
          const Sentry = require('@sentry/react');
          Sentry.metrics.distribution('cache.miss.fetch_time', fetchDuration, {
            unit: 'millisecond',
            tags: {
              dataType: this.getSessionType(sessionId),
              userType: 'beta',
            },
          });
        } catch (sentryError) {
          // Ignore Sentry errors to prevent breaking cache functionality
        }
      }

      // Cache the result (don't await, let it happen in background)
      this.set(sessionId, data).catch((err) =>
        console.warn(`[GenericCache] Failed to cache data for ${sessionId}:`, err)
      );

      return data;
    } catch (error) {
      console.error(`[GenericCache] Error in withCache for ${sessionId}:`, error);
      // Fallback to direct fetch if cache completely fails
      return fetcher();
    }
  }
}
